import React, { useEffect, useState, useMemo } from 'react';
import { db, auth } from '../services/firebase';
import { collection, addDoc, query, where, onSnapshot, serverTimestamp, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { Typography, Button, Card, CardContent, Box, List, ListItem, ListItemText, Chip, CircularProgress, Stack, TextField, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ptBR from 'date-fns/locale/pt-BR';
import styles from '../styles/Dashboard.module.css';
import { format, startOfMonth, endOfMonth, intervalToDuration } from 'date-fns';

const ControlePonto = () => {
  const [registros, setRegistros] = useState([]);
  const [ultimoRegistro, setUltimoRegistro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [condominioId, setCondominioId] = useState('');
  const [comentarioPonto, setComentarioPonto] = useState('');

  // Filtros de Data
  const [filtroDataInicio, setFiltroDataInicio] = useState(startOfMonth(new Date()));
  const [filtroDataFim, setFiltroDataFim] = useState(endOfMonth(new Date()));

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        // Busca o condomínio do usuário logado
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setCondominioId(userDoc.data().condominio_id);
        }
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    // Query para o histórico de registros COM FILTROS
    let historicoQuery = query(
      collection(db, 'tb_pontos'),
      where('funcionario_id', '==', currentUser.uid),
      orderBy('timestamp', 'desc')
    );

    // Obs: Firestore exige index composto para where(campo) + orderBy(outroCampo) ou where(range) + orderBy.
    // Como estamos ordenando por timestamp e filtrando por timestamp, geralmente funciona sem index extra se for o mesmo campo.
    // Mas aqui temos funcionario_id (equality) + timestamp (range/sort). Precisará de index composto.

    if (filtroDataInicio) {
      historicoQuery = query(historicoQuery, where('timestamp', '>=', filtroDataInicio));
    }
    if (filtroDataFim) {
      // Ajusta para o final do dia
      const fimDoDia = new Date(filtroDataFim);
      fimDoDia.setHours(23, 59, 59, 999);
      historicoQuery = query(historicoQuery, where('timestamp', '<=', fimDoDia));
    }

    const unsubscribeHistorico = onSnapshot(historicoQuery, (snapshot) => {
      const registrosList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRegistros(registrosList);
      setLoading(false);
    }, (error) => {
      console.error("Erro ao buscar registros de ponto:", error);
      // Fallback amigável se faltar index
      if (error.code === 'failed-precondition') {
        alert("Erro de permissão ou índice. Verifique o console.");
      }
      setLoading(false);
    });

    // Query separada e mais simples para buscar apenas o último registro (independente do filtro de data)
    const ultimoRegistroQuery = query(
      collection(db, 'tb_pontos'),
      where('funcionario_id', '==', currentUser.uid),
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    const unsubscribeUltimo = onSnapshot(ultimoRegistroQuery, (snapshot) => {
      setUltimoRegistro(snapshot.docs.length > 0 ? snapshot.docs[0].data() : null);
    });

    return () => {
      unsubscribeHistorico();
      unsubscribeUltimo();
    };
  }, [currentUser, filtroDataInicio, filtroDataFim]);

  const handleRegistrarPonto = async () => {
    setActionLoading(true);
    const tipo = ultimoRegistro?.tipo === 'entrada' ? 'saida' : 'entrada';

    try {
      await addDoc(collection(db, 'tb_pontos'), {
        funcionario_id: currentUser.uid,
        condominio_id: condominioId,
        timestamp: serverTimestamp(),
        tipo: tipo,
        comentario: comentarioPonto || '',
      });
      setComentarioPonto('');
    } catch (error) {
      console.error("Erro ao registrar ponto:", error);
      alert("Erro ao registrar ponto: " + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const horasTrabalhadas = useMemo(() => {
    if (registros.length < 2) {
      return '00:00:00';
    }

    // Ordenar cronologicamente (banco retorna desc)
    const registrosAsc = [...registros].reverse();
    let totalMilliseconds = 0;
    let ultimaEntrada = null;

    for (const registro of registrosAsc) {
      if (registro.tipo === 'entrada') {
        ultimaEntrada = registro.timestamp;
      } else if (registro.tipo === 'saida' && ultimaEntrada) {
        const saidaTime = registro.timestamp?.toDate ? registro.timestamp.toDate() : new Date(registro.timestamp);
        const entradaTime = ultimaEntrada?.toDate ? ultimaEntrada.toDate() : new Date(ultimaEntrada);

        if (saidaTime && entradaTime) {
          totalMilliseconds += (saidaTime.getTime() - entradaTime.getTime());
        }
        ultimaEntrada = null;
      }
    }

    if (totalMilliseconds <= 0) {
      return '00:00:00';
    }

    const duration = intervalToDuration({ start: 0, end: totalMilliseconds });
    const hours = String(duration.hours || 0).padStart(2, '0'); // Note: intervalToDuration doesn't accumulate hours > 24 well for total sum logic if simplified. 
    // Correction: intervalToDuration breaks down into days/hours. For total hours sum, better manual calc.

    // Better Calc for Total Hours across days:
    const totalHours = Math.floor(totalMilliseconds / (1000 * 60 * 60));
    const totalMinutes = Math.floor((totalMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const totalSeconds = Math.floor((totalMilliseconds % (1000 * 60)) / 1000);

    return `${String(totalHours).padStart(2, '0')}:${String(totalMinutes).padStart(2, '0')}:${String(totalSeconds).padStart(2, '0')}`;

  }, [registros]);

  const formatarDataHora = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Handle Firestore Timestamp or Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'dd/MM/yyyy HH:mm:ss');
  };

  const statusAtual = ultimoRegistro?.tipo === 'entrada' ? 'Trabalhando' : 'Fora de Serviço';
  const proximaAcao = ultimoRegistro?.tipo === 'entrada' ? 'Registrar Saída' : 'Registrar Entrada';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Controle de Ponto</Typography>

        <Card className={styles.card} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle} sx={{ textAlign: 'center' }}>Seu Status Atual</Typography>
            <Stack spacing={2} alignItems="center">
              <Chip
                label={statusAtual}
                color={statusAtual === 'Trabalhando' ? 'success' : 'default'}
                sx={{ fontSize: '1.2rem', p: 2, width: 'fit-content' }}
              />
              <TextField
                label="Adicionar um comentário (opcional)"
                fullWidth
                variant="outlined"
                size="small"
                value={comentarioPonto}
                onChange={(e) => setComentarioPonto(e.target.value)}
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleRegistrarPonto}
                disabled={actionLoading}
                fullWidth
              >
                {actionLoading ? <CircularProgress size={24} color="inherit" /> : proximaAcao}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Filters and Total */}
        <Card className={styles.card} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" className={styles.sectionTitle} gutterBottom>Filtro de Período</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <DatePicker
                    label="Data Início"
                    value={filtroDataInicio}
                    onChange={setFiltroDataInicio}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <DatePicker
                    label="Data Fim"
                    value={filtroDataFim}
                    onChange={setFiltroDataFim}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button fullWidth variant="outlined" onClick={() => { setFiltroDataInicio(null); setFiltroDataFim(null); }}>
                    Limpar
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>

            <Box sx={{ mt: 3, textAlign: 'center', p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">Total de Horas Trabalhadas</Typography>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {horasTrabalhadas}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Histórico de Registros</Typography>
            <List>
              {registros.map((registro) => (
                <ListItem key={registro.id} divider>
                  <ListItemText
                    primary={`Tipo: ${registro.tipo.charAt(0).toUpperCase() + registro.tipo.slice(1)}`}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">Data e Hora: {formatarDataHora(registro.timestamp)}</Typography>
                        {registro.comentario && <Typography component="span" variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>Comentário: {registro.comentario}</Typography>}
                      </>
                    }
                  />
                </ListItem>
              ))}
              {registros.length === 0 && <Typography sx={{ p: 2, textAlign: 'center' }}>Nenhum registro neste período.</Typography>}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ControlePonto;